/**
 * Product card broken-image diagnostic listener (ISS-04-093)
 *
 * The card's inline onerror (attached at HTML-parse time, so it always fires
 * first — well before this deferred module can register a live event
 * listener) records failures via a data-image-error-src attribute instead of
 * a plain DOM event, so no failure is missed regardless of when it happens:
 * - Already-failed wrappers (raced us) are caught by an initial sweep.
 * - Wrappers that fail later (e.g. lazy-loaded images scrolled into view
 *   after this module ran) are caught by a MutationObserver, since the
 *   data-image-error-src attribute is only ever added after this point.
 */

import { productCardImageErrorForm } from "./product-card-image-error-form";
import { ProductCardImageErrorHandler } from "./product-card-image-error-handler";

class ProductCardImageErrorListener {
    public handler: ProductCardImageErrorHandler;

    public constructor(handler?: ProductCardImageErrorHandler) {
        this.handler = handler || new ProductCardImageErrorHandler();
    }

    public setup(root: Document = document): void {
        productCardImageErrorForm.queryFailedImageWrappers(root).forEach((wrapper) => {
            void this.handler.handleFailedWrapper(wrapper);
        });

        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type !== "attributes" || !(mutation.target instanceof HTMLElement)) {
                    continue;
                }

                if (mutation.target.hasAttribute("data-image-error-src")) {
                    void this.handler.handleFailedWrapper(mutation.target);
                }
            }
        });

        observer.observe(root.body ?? root.documentElement, {
            attributes: true,
            attributeFilter: ["data-image-error-src"],
            subtree: true,
        });
    }
}

export { ProductCardImageErrorListener };
